import './styles.scss';
import React, {Dispatch, useContext, useEffect, useRef, useState} from "react";

/* Types, Constants, Utils */
import * as Constants from "../../../constants";
import {TFilter, TFilterOption, TFilterPivot} from "../../../types/search";
import {FiltersContext, IPageContext, PageContext} from "../../../types/app";

/* Bootstrap */
import {Accordion, Button, Collapse, Offcanvas} from "react-bootstrap";

/* Redux */
import {useDispatch, useSelector} from "react-redux";
import {ISearchDate, ISearchState} from "../../../types/redux/search";
import {RootState} from "../../../redux";
import {IResultsState} from "../../../types/redux/result";
import {
  applySelectedFilters,
  resetFilters,
  setAvailableFilterOptions, setCurrentPage, setFiltersLoaded,
  setSelectedFilters, suspendSearch
} from "../../../redux/slices/search-slice";
import {AnyAction} from "@reduxjs/toolkit";
import {ITagState, TSavedDocumentTag} from "../../../types/redux/saved";
import {clearResults, setResultsFacetFields} from "../../../redux/slices/result-slice";

/* api */
import SearchService from "../../../api/search";

/* Components */
import FiltersFilter from "../filters-filter";
import Spinner from "../../util/spinner";

/* hooks */
import {useQuery} from "react-query";
import useFilterOptions, {IUseFilterOptions} from "../hooks/useFilterOptions";
import FiltersApplied from "../filters-applied";
import {TAdvSearchDate} from "../../../constants";

interface IProps {
  label: string;
}

/* todo: next - break out slideout for base filter component */
const FiltersSlideout = (props: IProps):JSX.Element => {
  const {
    label
  } = props;

  //console.log('FiltersSlideout')
  /* redux */
  const dispatch:Dispatch<AnyAction> = useDispatch();
  const searchState: ISearchState = useSelector<RootState, ISearchState>(state => state.search);
  const tags:  TSavedDocumentTag[] = useSelector<RootState, ITagState>(state => state.tags).tags;
  const resultsState: IResultsState = useSelector<RootState, IResultsState>(state => state.result);
  const pageContext: string = useContext<IPageContext>(PageContext).context;

  /* state */
  const [availableFilters, setAvailableFilters] = useState<TFilter[]>([]);
  const [activeKeys, setActiveKeys] = useState<any>([Constants.DISPLAY, Constants.INDUSTRY]);
  const [show, setShow] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [expandedApplied, setExpandedApplied] = useState<boolean>(true);
  const [activeFiltersCnt, setActiveFiltersCnt] = useState<number>(0)

  /* refs */
  const filterOptions: IUseFilterOptions = useFilterOptions();
  const scrollContainerRef= useRef(null);
  const activeFiltersRef = useRef(null);
  const applyBtnContainerRef = useRef(null)
  const refetching = useRef<boolean>(false)

  const {
    data: facetQueryData,
    isSuccess: isFacetQuerySuccess,
    isFetching
  } = useQuery<any, Error>(
    [searchState.db_set,
              searchState.query,
              searchState.industry,
              "_facets"],
    async ({signal}) => {
      switch (searchState.filters_key) {
        case Constants.DOCUMENTS:
          return SearchService.solrDocumentQuery(JSON.parse(JSON.stringify(searchState)), undefined, true).then(response =>{
            return response
          })

        case Constants.BIBLIOGRAPHY:
          return await SearchService.solrBibliographyQuery(JSON.parse(JSON.stringify(searchState)), signal, true).then(response =>{
            return response
          })
      }
    },
    {
      staleTime: Infinity
    }
  );

  const {
    data: noDateQueryData,
    isSuccess: isNoDateQuerySuccess,
    refetch: noDateQueryRefetch,
  } = useQuery<any, Error>(
    [
      searchState.db_set,
      searchState.query,
      searchState.industry,
      "no_date"
    ],
    async ({signal}) => {
      switch (searchState.filters_key) {
        case Constants.DOCUMENTS:
          return await SearchService.solrDocumentQuery(JSON.parse(JSON.stringify(searchState)), undefined, true, true).then(response =>{
            return response
          })

        case Constants.BIBLIOGRAPHY:
          return await SearchService.solrBibliographyQuery(JSON.parse(JSON.stringify(searchState)), signal, false).then(response =>{
            return response
          })
      }
    },
    {
      staleTime: Infinity,
      enabled: false
    }
  );

  const availableFiltersOptions = (filters: TFilter[]): TFilterOption[] => {
    const _availableFilters: TFilter[] = JSON.parse(JSON.stringify(filters));
    let flattenedFilters: TFilterOption[] = [];
    _availableFilters.forEach(f=>{
      if (f.options.length > 0) {
        f.options.forEach(fo=>fo.filter_id = f.id)
        flattenedFilters = flattenedFilters.concat(f.options)
      } else if (f.pivot_options.length > 0){
        f.pivot_options.forEach(po=>{
          po.options.forEach(fo=>fo.filter_id = f.id)
          flattenedFilters = flattenedFilters.concat(po.options )
        })
      }
    })
    return flattenedFilters;
  }

  useEffect(()=>{
    if (!isNoDateQuerySuccess || !noDateQueryData ) return;

    const flattenedFilters:TFilterOption[] = availableFiltersOptions(availableFilters);
    const noDateIndex: number = flattenedFilters.findIndex(f=>f.id === Constants.NO_DATE);
    if (noDateIndex !== -1) {
      const filter: TFilterOption = flattenedFilters[noDateIndex];
      filter.count = noDateQueryData.response.numFound;
    }
    dispatch(setAvailableFilterOptions(flattenedFilters));
    dispatch(applySelectedFilters());

    if (searchState.suspendSearch) {
       /* loaded an url state that has a filter */
      dispatch(suspendSearch(false));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNoDateQuerySuccess, noDateQueryData,]);

  useEffect(()=> {
    if (!isFacetQuerySuccess ||  !facetQueryData) return;

    if (facetQueryData && facetQueryData.facet_counts) {
      dispatch(setResultsFacetFields({data:facetQueryData.facet_counts}));
    }

    if (facetQueryData && facetQueryData.facet_counts) {
      const cln: any = JSON.parse(JSON.stringify(facetQueryData.facet_counts));

      if (facetQueryData.facet_counts.facet_ranges.dddate) {
       const dateCounts: any = facetQueryData.facet_counts.facet_ranges.dddate.counts;
       let date: Date;
       let thresholdDate: Date = new Date(Constants.DATE_1950);
       let belowThresholdCount: number = 0;
       let lastEntryPos: number = 0;
       for (let i = 0; i < dateCounts.length; i++) {
         date = new Date(dateCounts[i]);
         if (date < thresholdDate) {
           belowThresholdCount += dateCounts[i + 1]
           lastEntryPos = i + 1;
         }
         i++;
       }

       const filterDateRanges: (string | number)[] = [...dateCounts].slice(lastEntryPos + 1, dateCounts.length);
       filterDateRanges.unshift(belowThresholdCount);
       filterDateRanges.unshift(Constants.DATE_LESS_THAN_1950);
       cln.facet_ranges.dddate.counts = filterDateRanges;
     }

     dispatch(setResultsFacetFields({data:cln}));
    }

    dispatch(setFiltersLoaded(true));
    refetching.current = false;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFacetQuerySuccess, facetQueryData])

  useEffect(()=>{
    if (isFetching) {
      setLoading(true)
      setAvailableFilters([]);
    }
  }, [isFetching])

  useEffect(()=>{
    if (availableFilters.length > 0){
      setLoading(false)
      dispatch(applySelectedFilters())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableFilters, setLoading]);

  useEffect(()=>{
    const activeFilters: TFilterOption[] = searchState.applied_filters.filter(fo=>{
      return fo.fq_group !== Constants.DISPLAY &&
        fo.fq_group !== Constants.DISPLAY2 &&
        fo.fq_group !== Constants.DISPLAY3
    })

    /* exclude advanced search date ranges */
    const dateFilters: ISearchDate[] = searchState.dates.filter((date:ISearchDate) =>{
      return !Constants.ADV_SEARCH_DATES.find((advDate:TAdvSearchDate) => date.id === advDate.id)
    });
    setActiveFiltersCnt(activeFilters.length + dateFilters.length);

    setExpandedApplied((activeFilters.length !== 0) || (searchState.dates.length !== 0));

    const setScrollHeight = ()=>{
      if (activeFiltersRef.current) {
        const height: number = applyBtnContainerRef.current.offsetTop - applyBtnContainerRef.current.clientHeight - activeFiltersRef.current.clientHeight - activeFiltersRef.current.clientTop;
        scrollContainerRef.current.style.height = height + 'px';
      }
    }
    setTimeout(()=> setScrollHeight(), 500); // 500 allows for transition to complete, otherwise refs are not accessible
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchState.applied_filters, searchState.dates, show ]); //activeFiltersCnt,

  const handleApply = (): void => {
    setShow(false);
    const _appliedFilters: TFilterOption[] = [...searchState.applied_filters];
    dispatch(applySelectedFilters());
    dispatch(setCurrentPage(1));

    setTimeout(()=>{
      if (JSON.stringify(searchState.applied_filters) !== JSON.stringify(_appliedFilters)) {
        dispatch(clearResults())
      }
    })
  }

  /* parse out filters from response data in a format the UI can consume */
  useEffect(()=>{
    if (!searchState.filters_key || (!resultsState.facet_fields && (searchState.filters_key !== Constants.TAGS))) return;

    const filters: TFilter[] = [];
    // @ts-ignore
    searchState.filters_db[searchState.filters_key].forEach((filterEntry: TFilter) => {
      const filter: TFilter = {...filterEntry}

      if (filter.facet_pivot) {
        filter.pivot_options = filterOptions.facetPivotOptions(filter, resultsState.facet_pivot[filter.facet_pivot]);
      } else if (filter.facet_ranges ) {
        filter.pivot_options = filterOptions.facetFieldsOptions(filter, resultsState.facet_ranges[filter.facet_ranges].counts) as TFilterPivot[];
      } else if (filter.facet_field) {
        filter.options = filterOptions.facetFieldsOptions(filter, resultsState.facet_fields[filter.facet_field]) as TFilterOption[];
      } else if (searchState.filters_key === Constants.TAGS) {
        filter.options = filterOptions.filterTagOptions(filter, tags);
      } else {
        filter.options = filterOptions.filterDBOptions(filter);
      }

      if ((filter.id === Constants.INDUSTRY.toLowerCase()) && (searchState.db_set === Constants.DOCUMENTS)){
        // populate industry field for all-industry collections filters
        filter.pivot_options.forEach(f=>{
          const industry: string = f.value.toLowerCase();
          f.options.forEach((fo: any) => {
            fo.industry = industry
          })
        })
      }

      /* add filter to available filters */
      if ((filter.options && (filter.options.length > 0)) || (filter.pivot_options && (filter.pivot_options.length > 0))) {
        if (searchState.industry === Constants.ALL_INDUSTRIES) {
          if (filter.id !== Constants.COLLECTION) {
            filters.push(filter)
          }
        } else {
          if (filter.id !== Constants.INDUSTRY) {
            filters.push(filter)
          }
        }
      }
    });

    setAvailableFilters(filters);
    setLoading(false);

    /* This is the case where filters need to be loaded before a search. Eg. Page refresh or Shared link */
    const displayFiltersId: string[] | undefined = searchState.filters_db.documents.find(f=>f.id === Constants.DISPLAY)?.options.map(fo=>fo.id);
    if (displayFiltersId) {
      const selectedNoDisplay = searchState.selected_filters_ids.filter(id => !displayFiltersId.includes(id));
      if ((selectedNoDisplay.length > 0) && (selectedNoDisplay.length !== (searchState.applied_filters.length + searchState.dates.length))) {
        handleApply();
      }
    }

    if (searchState.filters_key === Constants.TAGS) {
      dispatch(setAvailableFilterOptions(availableFiltersOptions(filters)));
    } else {
      noDateQueryRefetch();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsState.facet_fields, searchState.filters_key, tags])

  const handleExpand = (filterEntry: TFilter, index: number, value: boolean): void =>{
    const updatedFilters: TFilter[] = [...availableFilters];
    const filter: TFilter = {...filterEntry}
    filter.expanded = value;
    updatedFilters[index] = filter;
    setAvailableFilters(updatedFilters);
  }

  const handleToggleItem = (key: string):void => {
    const index = activeKeys.indexOf(key);
    if (index > -1) {
      activeKeys.splice(index, 1);
      setActiveKeys([...activeKeys]);
    } else {
      setActiveKeys(activeKeys.concat(key));
    }
  }

  const handleReset = (): void => {
    dispatch(resetFilters());
    dispatch(setCurrentPage(1))
  }

  const closeSlideout = ()=>{
    dispatch(setSelectedFilters(searchState.applied_filters.map(f=>f.id).concat(searchState.dates.map(d=>d.id))));
    setShow(false)
  }

  const updateDateOptions = (fqGroup: string,  id: string, value: any) =>{  // {pivot_options:[]}
    const availableFiltersCln: TFilter[] = [...availableFilters];
    let filter: TFilter = availableFilters.find(f => fqGroup === f.fq_group)
    let filterOption: TFilterPivot = filter.pivot_options.find(po => po.id === id);
    filterOption.options = value;

    let availableFilterOptions: TFilterOption[] = [...searchState.available_filter_options];
    availableFilterOptions = availableFilterOptions.concat(value)
    dispatch(setAvailableFilterOptions(availableFilterOptions));

    setAvailableFilters(availableFiltersCln);
  }

  return (
    <FiltersContext.Provider value={{updateDateOptions}}>
      <div className={'filters-slideout d-block text-nowrap'}>
        <Button
          id={'button-filters'}
          className={'border rounded-0 rounded-start shadow-sm pe-3' + ((pageContext !== Constants.MY_LIBRARY) ? ' border-end-0 ' : ' rounded-end')}
          variant={'light'}
          onClick={() => setShow(true)}
        >
          <i className={'bi bi-filter-left text-larger me-1'}/>
          <span className={'text-small align-middle'}>{label} {(activeFiltersCnt > 0) && '(' + activeFiltersCnt + ')'} </span>
        </Button>

        <Offcanvas
          className={'slideout'}
          show={show}
          onHide={() => closeSlideout()}>
          <Offcanvas.Header closeButton className={'pb-0 ps-4'}>
            <Offcanvas.Title><i className={'bi bi-filter-left text-larger me-1'}/> Filters</Offcanvas.Title>
          </Offcanvas.Header>

          <Offcanvas.Body  className={'px-0'}>
            <div ref={activeFiltersRef} id={'applied-filters'} className={'border-top border-bottom pt-3' + (activeFiltersCnt === 0 ? ' pb-2' : ' pb-0')}>
              <div className={'w-100 d-flex justify-content-between'}>

              <div className={'px-4'}>
                <Button className={'btn-icon-only'}
                  onClick={e=>{
                   if ((activeFiltersCnt > 0) || (searchState.dates.length > 0)) {
                     setExpandedApplied(!expandedApplied)
                   } else {
                     setExpandedApplied(false)
                   }
                  }}
                >
                  {!expandedApplied && <i className={'bi bi-chevron-down me-2 text-color-primary'}/>}
                  {expandedApplied && <i className={'bi bi-chevron-up me-2 text-color-primary'}/>}
                </Button>
                <span>Active Filters {'(' + activeFiltersCnt + ')'} </span>
              </div>

                <Button className={'link-clear-all btn-link p-0 text-xnormal me-4 text-color-primary cursor-pointer'} onClick={handleReset}>
                  Clear all
                </Button>
              </div>

              <Collapse in={expandedApplied} >
                <div  className={'applied-container px-3 mb-2 h-100'}>
                  <FiltersApplied/>
                </div>
              </Collapse>
            </div>

            <div ref={scrollContainerRef} id={'scroll-container'} className={'filters overflow-auto w-100'}>
              <Accordion className={'filters-checkboxes'} flush alwaysOpen activeKey={activeKeys}>
                {loading &&
                  <div className={'mt-4 text-center'}>
                    <Spinner size={Constants.MEDIUM}/>
                    <div className={'text-small p-4'}>
                      <p className={'p-0 m-0'} >Filters load after query results are available.</p>
                      <p className={'p-0 m-0 mt-2'} >This may take a moment... </p>
                    </div>
                  </div>}

                {/* for all filters */}
                {!loading && availableFilters.map((filter: TFilter, filterIndex: number) => {
                  return <FiltersFilter
                    key={filter.id + filterIndex}
                    opened={activeKeys.indexOf(filter.id) !== -1}
                    filter={{...filter}}
                    filterIndex={filterIndex}
                    handleToggleItem={handleToggleItem}
                    handleExpand={handleExpand}/>
                  })
                }
              </Accordion>
            </div>

            <div id={'apply-btn-container'}
              ref={applyBtnContainerRef}
              className={'py-3 px-4 position-absolute bottom-0 bg-light w-100 d-flex justify-content-center'}
            >
              {/* Apply */}
              <Button
                onClick = {()=>handleApply()}
                className={'text-small px-4  w-100'}
              >
                Apply Filters
              </Button>
            </div>

          </Offcanvas.Body>
        </Offcanvas>
      </div>

    </FiltersContext.Provider>
  )
}
export default FiltersSlideout;
